'use client';

import { X } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import CitationPreview from './citation-preview';
import CitationFormFields from './citation-form-fields';
import Button from '../../shared/Button';
import { InfoTooltip } from '../../shared/InfoTooltip';
import { generateReference, generateInText } from '../../../utils/citationGenerator';
import { mapBackendToFormFields, getFrontendSourceType } from '../../../utils/citationFieldMapper';
import {
	getLocatorTypeFromCitation,
	getLocatorLabel,
	getLocatorPlaceholder,
	getLocatorHelpText,
	formatLocatorValue,
	validateLocatorInput,
	LOCATOR_TYPES
} from '../../../utils/citationLocatorHelper';
import { validateCitationForm } from '../../../utils/citationValidation';
import { getFieldsForSource } from '../../../config/citation-config-new';
import Tooltip from '../../shared/Tooltip';
import TextareaWithCounter from '../../shared/TextareaWithCounter';
import { Toast } from '../../shared/Toast';
// import { citationCategories } from "../../../config/citation-config-new"

export default function AddCitationModal({
	isOpen,
	onClose,
	onAddCitation,
	editingCitation,
	// New props for insert mode
	mode = 'add', // "add" | "edit" | "insert"
	citationToInsert,
	onInsertCitation
}) {
	const [selectedCategory, setSelectedCategory] = useState('');
	const [selectedSource, setSelectedSource] = useState('');
	const [citationData, setCitationData] = useState({});
	const [validationErrors, setValidationErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Insert mode specific states
	const [citationStyle, setCitationStyle] = useState('');
	const [citationType, setCitationType] = useState('');
	const [directQuoteText, setDirectQuoteText] = useState('');
	const [directQuotePage, setDirectQuotePage] = useState(''); // Keeping for backward compatibility
	const [locatorValue, setLocatorValue] = useState('');

	const isEditing = mode === 'edit' || !!editingCitation;
	const isInserting = mode === 'insert';

	// Compute locator type based on citation (for insert mode)
	const locatorType = citationToInsert
		? getLocatorTypeFromCitation(
			citationToInsert.mainCategory || citationToInsert.category,
			citationToInsert.subcategory
		)
		: LOCATOR_TYPES.PAGE;

	// Define callback functions BEFORE any conditional returns (Rules of Hooks)
	const resetFormState = useCallback(() => {
		setSelectedCategory('');
		setSelectedSource('');
		setCitationData({});
		setValidationErrors({});
		setIsSubmitting(false);
		setCitationStyle('');
		setCitationType('');
		setDirectQuoteText('');
		setDirectQuotePage('');
		setLocatorValue('');
	}, []);

	const handleClose = useCallback(() => {
		resetFormState();
		onClose();
	}, [resetFormState, onClose]);

	// const handleBackdropClick = useCallback(
	// 	(e) => {
	// Only close if clicking the backdrop itself, not the modal content
	// 	if (e.target === e.currentTarget) {
	// 		handleClose();
	// 	}
	// 	},
	// 	[handleClose]
	// );

	// Handle modal state when opening
	useEffect(() => {
		if (isOpen) {
			// Clear validation errors when modal opens
			setValidationErrors({});

			// Reset loading state when modal opens
			setIsSubmitting(false);

			if (editingCitation && !isInserting) {
				// EDIT MODE: Pre-fill form with existing citation data
				const category = editingCitation.category || '';
				const sourceType = editingCitation.sourceType || '';

				console.log('Edit Modal - Category:', category);
				console.log('Edit Modal - Source Type:', sourceType);

				setSelectedCategory(category);
				setSelectedSource(sourceType);

				// Convert backend fields to frontend form fields
				const formDataForEdit = mapBackendToFormFields(editingCitation);
				setCitationData(formDataForEdit);
			} else if (isInserting) {
				// INSERT MODE: Reset only insert-specific states
				setCitationStyle('');
				setCitationType('');
				setDirectQuoteText('');
				setDirectQuotePage('');
				setLocatorValue('');
			} else {
				// ADD MODE: Reset all form fields for a fresh start
				setSelectedCategory('');
				setSelectedSource('');
				setCitationData({});
				setCitationStyle('');
				setCitationType('');
				setDirectQuoteText('');
				setDirectQuotePage('');
				setLocatorValue('');
			}
		}
	}, [isOpen, editingCitation, isInserting]);

	// Handle Escape key to close modal
	useEffect(() => {
		if (!isOpen) return;

		const handleEscapeKey = (e) => {
			if (e.key === 'Escape') {
				handleClose();
			}
		};

		document.addEventListener('keydown', handleEscapeKey);

		// Cleanup listener on unmount or when modal closes
		return () => {
			document.removeEventListener('keydown', handleEscapeKey);
		};
	}, [isOpen, handleClose]);

	if (!isOpen) return null;

	const handleCategoryChange = (category) => {
		setSelectedCategory(category);
		setSelectedSource('');
		setCitationData({});
		setValidationErrors({});
	};

	const handleSourceChange = (source) => {
		setSelectedSource(source);
		setCitationData({});
		setValidationErrors({});
	};

	const handleInputChange = (field, value) => {
		setCitationData((prev) => ({
			...prev,
			[field]: value
		}));
		// Clear validation error for this field when user makes changes
		if (validationErrors[field]) {
			setValidationErrors((prev) => {
				const updated = { ...prev };
				delete updated[field];
				return updated;
			});
		}
	};

	// Helper to get field aliases (e.g. author <-> authors)
	const getFieldAliases = (fieldName) => {
		const aliases = {
			author: ['authors'],
			authors: ['author'],
			editor: ['editors'],
			editors: ['editor'],
			translator: ['translators'],
			translators: ['translator']
		};
		return aliases[fieldName] || [];
	};

	const handleListAdd = (fieldName) => {
		setCitationData((prev) => {
			const currentList = prev[fieldName] || [];
			if (currentList.length >= 20) {
				setValidationErrors((prevErrors) => ({
					...prevErrors,
					[fieldName]: 'You cannot add more than 20 entries'
				}));
				return prev;
			}

			const newList = [...currentList, ''];
			const updates = { [fieldName]: newList };

			// Sync aliases
			getFieldAliases(fieldName).forEach(alias => {
				updates[alias] = newList;
			});

			return {
				...prev,
				...updates
			};
		});
	};

	const handleListRemove = (fieldName, index) => {
		setCitationData((prev) => {
			const newList = (prev[fieldName] || []).filter((_, i) => i !== index);
			const updates = { [fieldName]: newList };

			// Sync aliases
			getFieldAliases(fieldName).forEach(alias => {
				updates[alias] = newList;
			});

			return {
				...prev,
				...updates
			};
		});
	};

	const handleListUpdate = (fieldName, index, value) => {
		setCitationData((prev) => {
			const currentArray = prev[fieldName] || [''];
			const updatedArray = currentArray.map((item, i) => (i === index ? value : item));

			const updates = { [fieldName]: updatedArray };

			// Sync aliases
			getFieldAliases(fieldName).forEach(alias => {
				updates[alias] = updatedArray;
			});

			return {
				...prev,
				...updates
			};
		});
		// Clear validation error for this field when user makes changes
		if (validationErrors[fieldName]) {
			setValidationErrors((prev) => {
				const updated = { ...prev };
				delete updated[fieldName];
				return updated;
			});
		}
	};

	const handleAddCitation = async () => {
		// Start submitting
		setIsSubmitting(true);

		// Get the fields for the current source type to validate
		const fields = getFieldsForSource(selectedCategory, selectedSource);

		// Perform validation (pass selectedSource and selectedCategory for context-aware validation)
		const validation = validateCitationForm(fields, citationData, selectedSource, selectedCategory);

		if (!validation.isValid) {
			// Set validation errors to display
			setValidationErrors(validation.errors);
			// Stop submitting state
			setIsSubmitting(false);
			// Scroll to top to see error message
			const modalContent = document.querySelector('.lg\\:max-h-\\[850px\\]');
			if (modalContent) {
				modalContent.scrollTop = 0;
			}
			return; // Don't submit if validation fails
		}

		// Clear any previous errors
		setValidationErrors({});

		if (onAddCitation) {
			try {
				// Call parent handler and wait for it to complete
				await onAddCitation({
					category: selectedCategory,
					source: selectedSource,
					formData: validation.trimmedData // Use trimmed data
				});
				// If we reach here and modal is still open, reset state
				// (Parent will close modal on success via handleClose which also resets state)
			} catch (error) {
				// If parent throws error, reset submitting state so user can try again
				setIsSubmitting(false);
			}
		} else {
			// No handler provided, reset state
			setIsSubmitting(false);
		}
		// Note: isSubmitting will be reset when modal closes (handleClose calls resetFormState)
		// or above on error so user can try again
	};

	const handleInsertCitation = async () => {
		// Validate that citation style and type are selected
		// Validate that at least one of citation style or type is selected
		if (!citationStyle && !citationType) {
			Toast.error('Please select a Citation Style and Citation Type');
			return;
		}

		// Validate Direct Quote fields
		if (citationStyle === 'Direct Quote') {
			// Check if quote text is filled
			if (!directQuoteText || !directQuoteText.trim()) {
				Toast.error('Please enter the quote text for Direct Quote');
				return;
			}

			// Check if locator is filled (only if locator type is not NONE)
			if (locatorType !== LOCATOR_TYPES.NONE && (!locatorValue || !locatorValue.trim())) {
				const label = getLocatorLabel(locatorType).toLowerCase();
				Toast.error(`A locator is required for direct quotes. Enter ${label}.`);
				return;
			}

			// Validate format using helper
			if (locatorValue && locatorValue.trim()) {
				const validation = validateLocatorInput(locatorType, locatorValue);
				if (!validation.isValid) {
					Toast.error(validation.error);
					return;
				}
			}
		}

		// Start submitting
		setIsSubmitting(true);

		if (onInsertCitation && citationToInsert) {
			try {
				// Call parent handler and wait for it to complete
				await onInsertCitation({
					citation: citationToInsert,
					style: citationStyle, // "Paraphrase" or "Direct Quote"
					type: citationType, // "Parenthetical" or "Narrative"
					quote: directQuoteText, // The actual quote text
					page: directQuotePage, // Legacy field (backward compatibility)
					locator: locatorValue, // User-entered locator: "23", "4:15", "Introduction"
					locatorType: locatorType // Type: "page", "timestamp", "paragraph_or_section", etc.
				});
				// If we reach here and modal is still open, reset state
				// (Parent will close modal on success via handleClose which also resets state)
			} catch (error) {
				// If parent throws error, reset submitting state so user can try again
				setIsSubmitting(false);
			}
		} else {
			// No handler provided, reset state
			setIsSubmitting(false);
		}
		// Note: isSubmitting will be reset when modal closes (handleClose calls resetFormState)
		// or above on error so user can try again
	};

	// Get citation details for insert mode
	const getInsertCitationDetails = () => {
		if (!citationToInsert) return { sourceType: '', primaryAuthor: '', fullCitation: '', category: '' };

		const { category, subcategory, sourceType, authors } = citationToInsert;
		const frontendSourceType = getFrontendSourceType(subcategory || sourceType, citationToInsert);
		const formFields = mapBackendToFormFields(citationToInsert);

		// Generate full reference using dynamic citation generator
		const fullCitation =
			category && frontendSourceType
				? generateReference(category, frontendSourceType, formFields).replace(/<[^>]*>/g, '')
				: 'Invalid Citation Type';

		const primaryAuthor = authors && authors.length > 0 ? authors[0] : 'Unknown Author';

		return { sourceType: frontendSourceType, primaryAuthor, fullCitation, category };
	};

	// Generate in-text citation preview
	const generateInTextPreview = () => {
		if (!citationToInsert) return '';
		if (!citationStyle || !citationType) return 'Select paraphrase or direct quote, then narrative or parenthetical, to preview.';

		const { category, subcategory, sourceType } = citationToInsert;
		const frontendSourceType = getFrontendSourceType(subcategory || sourceType, citationToInsert);
		const formFields = mapBackendToFormFields(citationToInsert);

		// Generate in-text citations using dynamic citation generator
		const inTextCitations =
			category && frontendSourceType
				? generateInText(category, frontendSourceType, formFields)
				: { parenthetical: '(Author, n.d.)', narrative: 'Author (n.d.)' };

		const quoteText =
			directQuoteText && directQuoteText.trim().length > 0
				? directQuoteText.trim().replace(/\.+$/, '')
				: 'Quote here....';

		// Check for block quote (40+ words)
		const wordCount = quoteText !== 'Quote here....' ? quoteText.split(/\s+/).filter(w => w.length > 0).length : 0;
		const isBlockQuote = wordCount >= 40;

		// Format the locator based on type (smart auto-formatting!)
		const formattedLocator = locatorValue
			? formatLocatorValue(locatorType, locatorValue)
			: locatorType === LOCATOR_TYPES.PAGE
				? 'p. 45'
				: 'locator'; // Default placeholder

		if (citationType === 'Narrative') {
			if (citationStyle === 'Direct Quote') {
				// For direct quotes, modify the narrative citation to include quote and locator
				const narrativeParts = inTextCitations.narrative.split('(');
				const author = narrativeParts[0].trim();
				const yearMatch = inTextCitations.narrative.match(/\(([^)]+)\)/);
				const year = yearMatch ? yearMatch[1] : 'n.d.';

				// Add locator if provided
				const locatorPart = locatorValue ? ` (${formattedLocator})` : '';

				if (isBlockQuote) {
					// Block Quote Preview: Indented, no quotation marks
					return (
						<>
							{author} ({year}) explained:
							<div>
								{quoteText} <span className="citation">{locatorPart}</span>
							</div>
						</>
					);
				}

				return `${author} (${year}) explained, "${quoteText}"${locatorPart}.`;
			}
			// return `${inTextCitations.narrative} found this.`;
			return `${inTextCitations.narrative}`;

		} else {
			// Parenthetical
			if (citationStyle === 'Direct Quote') {
				// For direct quotes, add locator to parenthetical citation
				const locatorPart = locatorValue ? `, ${formattedLocator}` : '';
				const citationWithLocator = inTextCitations.parenthetical.replace(')', `${locatorPart})`);

				if (isBlockQuote) {
					// Block Quote Preview: Indented, no quotation marks, citation at end
					return (
						<div>
							{quoteText} {citationWithLocator}
						</div>
					);
				}

				return `"${quoteText}" ${citationWithLocator}.`;
			}
			//comment Example sentence with
			return `${inTextCitations.parenthetical}.`;
		}
	};

	const { sourceType, primaryAuthor, fullCitation, category } = getInsertCitationDetails();

	// Get heading and description based on mode
	const getHeading = () => {
		if (isInserting) return 'Insert citation';
		if (isEditing) return 'Edit citation';
		return 'Add source';
	};

	const getDescription = () => {
		if (isInserting) return 'Choose how this source appears in your paper.';
		if (isEditing) return 'Update the details below';
		return 'Select a category and source type to begin.';
	};

	const getButtonText = () => {
		if (isSubmitting) {
			if (isInserting) return 'Inserting...';
			if (isEditing) return 'Updating...';
			return 'Adding...';
		}

		if (isInserting) return 'Insert citation';
		if (isEditing) return 'Update citation';
		return 'Add source';
	};

	const handleSubmit = () => {
		if (isInserting) {
			handleInsertCitation();
		} else {
			handleAddCitation();
		}
	};

	const isButtonDisabled =
		isSubmitting ||
		(isInserting
			? !citationStyle || !citationType
			: !selectedCategory || !selectedSource);

	// Render Insert Mode Content
	const renderInsertContent = () => (
		<>
			{/* Selected Source */}
			<div className="mb-6">
				<div className="flex items-center gap-2 mb-3">
					<span className="text-sm font-bold text-dark-blue">Selected Source</span>
					<Tooltip placement='bottom' content="The source of the citation. This will determine the available source types and fields.">
						<InfoTooltip />
					</Tooltip>
				</div>

				{/* Citation Card Preview */}
				<div className="rounded-[10px] p-[1px] bg-gradient-to-r from-orange to-purple">
					<div className="bg-white rounded-[9px] p-4 relative">
						{/* <button
							onClick={handleClose}
							className="absolute top-3 right-3 w-5 h-5 bg-gray/20 hover:bg-gray/40 rounded-full flex items-center justify-center transition-colors"
						>
							<X className="w-3 h-3 text-gray" />
						</button> */}
						<span className="text-[#686576] text-10 font-medium leading-100 mb-2 block">
							{category} | {sourceType}
						</span>
						<h3 className="text-sm leading-100 font-bold text-dark-blue mb-2">{primaryAuthor}</h3>
						<div className="border-l-[1.5px] border-[#F0F0F0] pl-3">
							<p className="text-xs text-dark-blue leading-[18px] font-medium">{fullCitation}</p>
						</div>
					</div>
				</div>
			</div>

			{/* Citation Style */}
			<div className="mb-6">
				<div className="flex items-center gap-2 mb-3">
					<span className="text-sm font-bold text-dark-blue">Quotation style</span>
					<Tooltip content="Choose between paraphrasing the source material or using a direct quote from the source.">
						<InfoTooltip />
					</Tooltip>
				</div>
				<div className="flex gap-6">
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="radio"
							name="citationStyle"
							value="Paraphrase"
							checked={citationStyle === 'Paraphrase'}
							onChange={(e) => setCitationStyle(e.target.value)}
							className="w-4 h-4 text-purple accent-purple"
						/>
						<span className="text-sm text-dark-blue">Paraphrase</span>
					</label>
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="radio"
							name="citationStyle"
							value="Direct Quote"
							checked={citationStyle === 'Direct Quote'}
							onChange={(e) => setCitationStyle(e.target.value)}
							className="w-4 h-4 text-purple accent-purple"
						/>
						<span className="text-sm text-dark-blue">Direct Quote</span>
					</label>
				</div>
			</div>

			{/* Direct Quote extra fields */}
			{citationStyle === 'Direct Quote' && (
				<div className="mb-6 space-y-4">
					<div>
						<div className="flex items-center gap-2 mb-2">
							<span className="text-sm font-bold text-dark-blue">Quote</span>
							<Tooltip placement="right" content="Enter the exact text you're quoting from the source. Use quotation marks in your actual document.">
								<InfoTooltip />
							</Tooltip>
						</div>
						<TextareaWithCounter
							className="w-full rounded-[12px] border border-[#F0F0F0] px-4 py-3 text-sm text-dark-blue placeholder:text-gray-500 focus:outline-none focus:border-purple resize-none"
							rows={5}
							minLength={3}
							maxLength={5000}
							placeholder="Enter the exact wording from the source."
							value={directQuoteText}
							onChange={(e) => setDirectQuoteText(e.target.value)}
						/>
						{/* Word Count Indicator removed per user request */}
						<div className="flex justify-end items-start mt-2">
							{directQuoteText.trim().split(/\s+/).filter(w => w.length > 0).length >= 40 && (
								<div className="p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800 w-full">
									<strong>Block Quote Detected:</strong> Text contains 40+ words. This will be inserted as an indented block quote.
								</div>
							)}
						</div>
					</div>

					{/* Dynamic Locator Field - Based on Source Type! */}
					{locatorType !== LOCATOR_TYPES.NONE && (
						<div>
							<div className="flex items-center gap-2 mb-2">
								<span className="text-sm font-bold text-dark-blue">
									{getLocatorLabel(locatorType)}
								</span>
								<Tooltip placement="top" content={getLocatorHelpText(locatorType)}>
									<InfoTooltip />
								</Tooltip>
							</div>
							<input
								type="text"
								className="w-full h-[44px] rounded-[12px] border border-[#F0F0F0] px-4 text-sm text-dark-blue placeholder:text-gray-500 focus:outline-none focus:border-purple"
								placeholder={getLocatorPlaceholder(locatorType)}
								value={locatorValue}
								onChange={(e) => setLocatorValue(e.target.value)}
							/>
							{/* Show formatting hint */}
							{locatorValue && (
								<p className="text-xs text-gray-500 mt-1">
									Appears as:
									<span className="font-semibold">
										{formatLocatorValue(locatorType, locatorValue)}
									</span>
								</p>
							)}
						</div>
					)}
				</div>
			)}

			{/* Citation Type */}
			<div>
				<div className="flex items-center gap-2 mb-3">
					<span className="text-sm font-bold text-dark-blue">Citation type</span>
					<Tooltip content="Narrative citations incorporate the author into the sentence, while parenthetical citations place the author and year in parentheses at the end.">
						<InfoTooltip />
					</Tooltip>
				</div>
				<div className="flex gap-6">
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="radio"
							name="citationType"
							value="Narrative"
							checked={citationType === 'Narrative'}
							onChange={(e) => setCitationType(e.target.value)}
							className="w-4 h-4 text-purple accent-purple"
						/>
						<span className="text-sm text-dark-blue">Narrative</span>
					</label>
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="radio"
							name="citationType"
							value="Parenthetical"
							checked={citationType === 'Parenthetical'}
							onChange={(e) => setCitationType(e.target.value)}
							className="w-4 h-4 text-purple accent-purple"
						/>
						<span className="text-sm text-dark-blue">Parenthetical</span>
					</label>
				</div>
			</div>
		</>
	);

	// Render Insert Mode Preview
	const renderInsertPreview = () => (
		<div className="lg:pl-8">
			<h2 className="text-2xl font-bold text-dark-blue mb-2">Live Preview</h2>
			<p className="text-gray text-sm mb-6">APA in-text citation preview</p>

			{/* In-text examples */}
			<div className="mb-4">
				<span className="text-sm font-bold text-dark-blue block mb-3">  {citationStyle && citationType ? 'In-text citation' : 'In-text example'}
				</span>
				<div className="border-l-[2px] border-purple pl-4 py-2 bg-[#FAFAFA] rounded-r-lg">
					<p className="text-sm text-dark-blue italic break-words">{generateInTextPreview()}</p>
				</div>
			</div>
		</div>
	);

	return (
		<div
			className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto lg:overflow-visible"
		// onClick={handleBackdropClick}
		>
			{/* Backdrop */}
			<div className="absolute inset-0 bg-black/70" />

			{/* Modal */}
			<div className="relative z-10 w-full max-w-[900px] rounded-[30px] bg-white shadow-2xl p-5 sm:p-6 md:p-[40px] max-h-[90vh] overflow-y-auto lg:max-h-none lg:overflow-visible">
				{/* Close button */}
				<button onClick={handleClose} className="absolute top-4 right-4 text-dark-blue transition">
					<X className="w-5 h-5 text-dark-blue" />
				</button>

				{/* Main content */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 relative">
					{/* Vertical divider */}
					<div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-[1px] bg-[#F0F0F0] transform -translate-x-1/2" />

					{/* Left Column */}
					<div className="lg:flex lg:flex-col lg:max-h-[80vh]">
						{/* Header - Same for all modes */}
						<h2 className="text-2xl font-bold text-dark-blue mb-2">{getHeading()}</h2>
						<p className="text-gray text-sm xl:mb-6 mb-2">{getDescription()}</p>

						{/* Top divider */}
						<div className="hidden md:block h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2" />

						{/* Content - Different based on mode */}
						{isInserting ? (
							<div className="lg:max-h-[850px] lg:min-h-[320px] lg:overflow-y-auto pr-3 custom-scrollbar">
								{renderInsertContent()}
							</div>
						) : (
							<div className="lg:max-h-[850px] lg:min-h-[320px] lg:overflow-y-auto pr-3 custom-scrollbar">
								{/* Display global validation error message */}
								{Object.keys(validationErrors).length > 0 && (
									<div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
										<p className="text-sm font-semibold text-red-700 mb-2">
											Please fix the following errors:
										</p>
										<ul className="list-disc list-inside text-sm text-red-600 space-y-1">
											{Object.entries(validationErrors).map(([field, error]) => (
												<li key={field}>{error}</li>
											))}
										</ul>
									</div>
								)}
								<CitationFormFields
									selectedCategory={selectedCategory}
									selectedSource={selectedSource}
									citationData={citationData}
									validationErrors={validationErrors}
									isEditing={isEditing}
									onCategoryChange={handleCategoryChange}
									onSourceChange={handleSourceChange}
									onInputChange={handleInputChange}
									onListAdd={handleListAdd}
									onListRemove={handleListRemove}
									onListUpdate={handleListUpdate}
								/>
							</div>
						)}

						{/* Bottom divider */}
						<div className="hidden md:block h-[1px] bg-[#F0F0F0] xl:my-[25px] my-2" />

						{/* Button - Same structure for all modes */}
						<Button onClick={handleSubmit} disabled={isButtonDisabled} className="py-4">
							{getButtonText()}
						</Button>
					</div>

					{/* Right Column - Preview */}
					{isInserting ? (
						renderInsertPreview()
					) : (
						<CitationPreview
							selectedCategory={selectedCategory}
							selectedSource={selectedSource}
							citationData={citationData}
						/>
					)}
				</div>
			</div>
		</div>
	);
}
